import { useState } from 'react'
import { BookOpen, PlugZap } from 'lucide-react'
import { EmptyState, SegmentedControl, Skeleton } from '@teach/ui'
import { useGetConnectionsQuery, useRevokeConnectionMutation } from '../mcpApi'
import { ConnectionRow } from '../components/ConnectionRow'
import { ConnectGuide } from '../components/ConnectGuide'
import { useIsMobile } from '@/hooks/use-mobile'
import { useMessages } from '@/i18n'
import { messages } from '../messages'

type View = 'guide' | 'connected'

export const Connections = () => {
  const [view, setView] = useState<View>('guide')
  const m = useMessages(messages)
  const isMobile = useIsMobile()
  const { data, isLoading, isError } = useGetConnectionsQuery()
  const [revoke, { isLoading: revoking }] = useRevokeConnectionMutation()

  const onRevoke = (clientId: string) => {
    // 204 → cache invalidation refetches; 404 (already gone) → tag invalidation
    // still refetches, so no explicit handling needed.
    void revoke(clientId)
  }

  return (
    // Контейнер и `h1` живут в `SettingsLayout`: страница стала разделом
    // настроек, а не отдельным экраном, и своя центровка давала двойные
    // отступы и второй `h1` на странице.
    <section>
      <h2 className="sr-only">{m.connectors}</h2>

      {/* На телефоне — во всю ширину: капсула в полэкрана выглядит недоделанной. */}
      <SegmentedControl<View>
        className="mb-6"
        fullWidth={isMobile}
        value={view}
        onChange={setView}
        options={[
          { id: 'guide', label: m.howToConnect, icon: BookOpen },
          { id: 'connected', label: m.connectedTab, icon: PlugZap, count: data?.length },
        ]}
      />

      {view === 'guide' ? (
        <ConnectGuide />
      ) : isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full rounded-xl" />
          <Skeleton className="h-16 w-full rounded-xl" />
        </div>
      ) : isError ? (
        <EmptyState title={m.loadFailed} description={m.loadFailedHint} />
      ) : !data || data.length === 0 ? (
        <EmptyState title={m.noConnections} />
      ) : (
        <div className="space-y-2">
          {data.map((conn) => (
            <ConnectionRow
              key={conn.client_id}
              conn={conn}
              onRevoke={onRevoke}
              revoking={revoking}
            />
          ))}
        </div>
      )}
    </section>
  )
}
